import type { TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback, useMemo } from 'react';
import { DEFAULT_DIALOG_SORTING } from '../constants';
import { isEmptyFilterValue } from './filterValue';
import type { DescriptionActions } from './types';

/** Параметры управления отбором из его описания */
type Params = {
  /** Действующая сортировка — та же, что названа в описании */
  sorting: TableSortingState;
  /** Записывает сортировку таблицы */
  onSortingChange: (next: TableSortingState) => void;
  /** Действующие фильтры — те же, что перечислены в описании */
  filters: TableFilterState;
  /** Записывает фильтры таблицы */
  onFiltersChange: (next: TableFilterState) => void;
  /** Выборку перехода можно снять — у сохранённой пресетом снятие живёт в строке пресета */
  canRemoveSelection: boolean;
  /** Снимает выборку перехода целиком */
  onRemoveSelection: () => void;
};

/**
 * Управление отбором из его описания: переключение направления сортировки, снятие сортировки,
 * фильтра и выборки перехода. Пишет то же состояние таблицы, что и контролы в шапке колонки,
 * поэтому описание и таблица всегда показывают один и тот же отбор.
 */
export const useDescriptionActions = ({
  sorting,
  onSortingChange,
  filters,
  onFiltersChange,
  canRemoveSelection,
  onRemoveSelection,
}: Params): DescriptionActions => {
  const onToggleSortDirection = useCallback(
    (columnId: string) => {
      // Пустая сортировка — это умолчание списка: переключение направления делает его
      // выбором пользователя, поэтому в состояние уходит уже явная сортировка.
      const current = sorting.length ? sorting : DEFAULT_DIALOG_SORTING;
      onSortingChange(current.map((sort) => (sort.id === columnId ? { ...sort, desc: !sort.desc } : sort)));
    },
    [sorting, onSortingChange],
  );

  const onClearSorting = useCallback(
    (columnId: string) => {
      onSortingChange(sorting.filter((sort) => sort.id !== columnId));
    },
    [sorting, onSortingChange],
  );

  const onRemoveFilter = useCallback(
    (filterId: string) => {
      onFiltersChange(filters.filter((filter) => filter.id !== filterId));
    },
    [filters, onFiltersChange],
  );

  const onChangeFilter = useCallback(
    (filterId: string, value: unknown) => {
      // Опустошённый фильтр не отбирает ничего — в состоянии таблицы его быть не должно
      if (isEmptyFilterValue(value)) {
        onFiltersChange(filters.filter((filter) => filter.id !== filterId));
        return;
      }
      const hasFilter = filters.some((filter) => filter.id === filterId);
      onFiltersChange(
        hasFilter
          ? filters.map((filter) => (filter.id === filterId ? { ...filter, value } : filter))
          : [...filters, { id: filterId, value }],
      );
    },
    [filters, onFiltersChange],
  );

  return useMemo(
    () => ({
      onToggleSortDirection,
      onClearSorting,
      onRemoveFilter,
      onChangeFilter,
      onRemoveSelection: canRemoveSelection ? onRemoveSelection : null,
    }),
    [onToggleSortDirection, onClearSorting, onRemoveFilter, onChangeFilter, canRemoveSelection, onRemoveSelection],
  );
};
